import { useState, type ReactNode } from 'react'
import { Barcode, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, EyeOff, Layers, Link2, Plus, RefreshCw, Unlink } from 'lucide-react'

import { ActionInfo, Button, Highlight, ProductThumb, TextField } from '@krp/ui-kit'

import { thumbOf, type ProductPhotoUrl } from '../../../shared/product'
import { QueryState } from '../../../shared/QueryState'
import type { CommonCatalogActions, CommonCatalogRowVm, CommonCatalogVm } from '../model/types'
import './commonCatalog.css'

/**
 * «Номенклатура → Общий каталог» (UI-SECTIONS): товары нашего сервера и ресурсы,
 * с которыми каждый связан. Экран получает vm и actions и больше ничего (D-053).
 * «Не связан» и «есть скрытые связи» различаются значком и словом, а не цветом.
 *
 * Общий каталог — эталон (RP-FB50): нажатие на наименование открывает окно «Общий товар» (просмотр и правка), «Создать товар»
 * заводит новый общий товар. Карточки источников — на вкладках источников.
 */
export function CommonCatalogScreen({
  vm,
  actions,
  photoUrl,
  sources,
  sourceNames,
}: {
  vm: CommonCatalogVm
  actions: CommonCatalogActions
  /** Адрес главного фото карточки товара: нажатие открывает его на весь экран. */
  photoUrl?: ProductPhotoUrl
  /** Раскрытый список «Источники» (RP-SE13): его собирает binding из пакетов видов. */
  sources?: ReactNode
  /** Имена источников для свёрнутой строки-триггера; пусто — источников нет, триггера и панели нет. */
  sourceNames?: readonly string[]
}) {
  const ready = vm.query.status === 'ready'
  const [sourcesOpen, setSourcesOpen] = useState(false)
  return (
    <section aria-labelledby="platform-catalog-title" className="platform-catalog">
      <header className="platform-catalog__toolbar">
        <div className="platform-catalog__heading">
          <h2 id="platform-catalog-title">Общий каталог</h2>
        </div>
        <div className="platform-catalog__toolbar-actions">
          {sourceNames !== undefined && sourceNames.length > 0 && (
            <ActionInfo id="platform.catalog.sources" label="Источники">
              <button
                type="button"
                className="platform-catalog__sources-toggle"
                data-testid="platform-sources-toggle"
                aria-expanded={sourcesOpen}
                aria-controls="platform-catalog-sources"
                aria-label={`Источники: ${sourceNames.join(', ')}`}
                onClick={() => setSourcesOpen((open) => !open)}
              >
                <Layers size={16} aria-hidden="true" />
                <span className="platform-catalog__sources-label">Источники</span>
                <span className="platform-catalog__sources-names" aria-hidden="true">
                  {sourceNames.join(', ')}
                </span>
                {sourcesOpen ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
              </button>
            </ActionInfo>
          )}
          <Button
            variant="primary"
            icon={<Plus size={16} aria-hidden="true" />}
            onClick={actions.createProduct}
            actionId="platform.catalog.create"
          >
            Создать товар
          </Button>
          <Button
            variant="ghost"
            icon={<RefreshCw size={16} aria-hidden="true" />}
            busy={vm.query.refreshing}
            onClick={actions.reload}
            actionId="platform.catalog.reload"
          >
            Обновить
          </Button>
        </div>
      </header>

      {sourcesOpen && sourceNames !== undefined && sourceNames.length > 0 && (
        <div id="platform-catalog-sources" className="platform-catalog__sources-panel">
          {sources}
        </div>
      )}

      <div className="platform-catalog__search" role="search">
        <TextField
          type="search"
          label="Поиск"
          placeholder="Наименование, артикул или штрихкод"
          value={vm.search}
          onChange={actions.searchChanged}
        />
      </div>

      <QueryState query={vm.query} emptyText={vm.emptyText} onRetry={actions.reload} />

      {ready && (
        <>
          <p className="platform-catalog__count" aria-live="polite">
            {vm.rangeText}
            {vm.query.refreshing && ' · обновляется…'}
          </p>
          <div className="platform-catalog__table-wrap">
            <table className="platform-catalog__table" aria-describedby="platform-catalog-title">
              <thead>
                <tr>
                  <th scope="col">Наименование</th>
                  <th scope="col">Артикул</th>
                  <th scope="col">Источники</th>
                  <th scope="col">
                    <span className="platform-catalog__sr">Действия</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {vm.rows.map((row) => (
                  <CatalogRow
                    key={row.id}
                    row={row}
                    query={vm.search}
                    onOpenLinks={actions.openLinks}
                    onOpenProduct={actions.openProduct}
                    photoUrl={photoUrl}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {vm.pages > 1 && (
            <nav className="platform-catalog__pager" aria-label="Страницы каталога">
              <Button
                variant="secondary"
                icon={<ChevronLeft size={16} aria-hidden="true" />}
                disabled={!vm.canPrevious}
                onClick={() => actions.pageChanged(vm.page - 1)}
                actionId="platform.catalog.page"
                disabledReason={vm.canPrevious ? null : 'Это первая страница.'}
              >
                Назад
              </Button>
              <span aria-current="page">
                Страница {vm.page} из {vm.pages}
              </span>
              <Button
                variant="secondary"
                icon={<ChevronRight size={16} aria-hidden="true" />}
                disabled={!vm.canNext}
                onClick={() => actions.pageChanged(vm.page + 1)}
                actionId="platform.catalog.page"
                disabledReason={vm.canNext ? null : 'Это последняя страница.'}
              >
                Дальше
              </Button>
            </nav>
          )}
        </>
      )}
    </section>
  )
}

function CatalogRow({
  row,
  query,
  onOpenLinks,
  onOpenProduct,
  photoUrl,
}: {
  row: CommonCatalogRowVm
  query: string
  onOpenLinks: (resourceId: string | null) => void
  onOpenProduct: (productId: string) => void
  photoUrl: ProductPhotoUrl | undefined
}) {
  const details = [
    row.sharedName !== null ? `общее имя: ${row.sharedName}` : null,
    row.unitCode !== null ? `ед.: ${row.unitCode}` : null,
  ].filter((part): part is string => part !== null)
  return (
    <tr>
      <td data-label="Наименование">
        <div className="platform-catalog__product">
          <ProductThumb src={thumbOf(row.commonPhotoUrl, row.refs, photoUrl)} name={row.name} size={48} />
          <div className="platform-catalog__title">
            <div className="platform-catalog__nameline">
              <ActionInfo id="platform.catalog.open" label={row.name}>
                <button
                  type="button"
                  className="platform-catalog__name platform-catalog__open"
                  onClick={() => onOpenProduct(row.id)}
                >
                  <Highlight text={row.name} query={query} />
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </ActionInfo>
              {row.barcodeCount > 0 && (
                <span
                  className="platform-catalog__barcodes"
                  title={`Штрихкодов у товара: ${row.barcodeCount}`}
                  aria-label={`Штрихкодов у товара: ${row.barcodeCount}`}
                  tabIndex={0}
                >
                  <Barcode size={14} aria-hidden="true" />
                  {row.barcodeCount}
                </span>
              )}
            </div>
            {details.length > 0 && <span className="platform-catalog__details">{details.join(' · ')}</span>}
          </div>
        </div>
      </td>
      <td data-label="Артикул" className="platform-catalog__sku">
        {row.sku !== null ? <Highlight text={row.sku} query={query} /> : <span aria-label="артикула нет">—</span>}
      </td>
      <td data-label="Источники">
        {row.linked ? (
          <ul className="platform-catalog__sources" aria-label={`Источники товара ${row.name}`}>
            {row.sources.map((source) => (
              <li
                key={source.resourceId}
                className="platform-catalog__source"
                title={`${source.kindTitle} · ключ ${source.remoteKey} · ${source.originText}`}
              >
                <Link2 size={14} aria-hidden="true" />
                <span>{source.name}</span>
                <span className="platform-catalog__sr">
                  ({source.kindTitle}, ключ {source.remoteKey}, {source.originText})
                </span>
              </li>
            ))}
            {row.hiddenCount > 0 && (
              <li className="platform-catalog__source" data-hidden="true">
                <EyeOff size={14} aria-hidden="true" />
                <span>ещё {row.hiddenCount} — нет доступа</span>
              </li>
            )}
          </ul>
        ) : (
          <span className="platform-catalog__unlinked">
            <Unlink size={14} aria-hidden="true" />
            не связан
          </span>
        )}
      </td>
      <td className="platform-catalog__actions">
        <Button
          variant="ghost"
          icon={<Link2 size={16} aria-hidden="true" />}
          aria-label={`Связи товара ${row.name}`}
          onClick={() => onOpenLinks(row.linksResourceId)}
          actionId="platform.catalog.links"
        >
          Связи
        </Button>
      </td>
    </tr>
  )
}
